import { useEffect, useRef, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { API_BASE_URL } from '@/api/client'
import { RealtimeApplicationToast } from '@/components/realtime-application-toast'
import { IncomingCallCard } from '@/components/incoming-call-card'
import type { IncomingCall } from '@/components/incoming-call-card'
import type { CallEndedPayload, CallIncomingPayload } from '@/types/api'

type InvalidatePayload = {
  eventId?: string
  applicationId?: number
  squareIds: number[]
  currentSquareIds: number[]
  refreshDashboard: boolean
  refreshApplicationDetail: boolean
}

type ApplicationCreatedPayload = {
  eventId?: string
  applicationId: number
  applicationNumber?: number
  path: string
  schemaName?: string
  squareIds: number[]
  squareNames: string[]
  stageName?: string
}

const DEDUPE_LIMIT = 200

function rememberEvent(
  seenEventIds: Set<string>,
  orderedEventIds: string[],
  eventId?: string,
) {
  if (!eventId) {
    return false
  }
  if (seenEventIds.has(eventId)) {
    return true
  }

  seenEventIds.add(eventId)
  orderedEventIds.push(eventId)
  if (orderedEventIds.length > DEDUPE_LIMIT) {
    const expiredEventId = orderedEventIds.shift()
    if (expiredEventId) {
      seenEventIds.delete(expiredEventId)
    }
  }
  return false
}

export function softRefreshCrmQueries(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['bootstrap'] }),
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    queryClient.invalidateQueries({
      predicate: (query) => {
        const scope = query.queryKey[0]
        return (
          scope === 'square-detail' ||
          scope === 'current-square' ||
          scope === 'application-detail'
        )
      },
    }),
  ])
}

export function invalidateRealtimeQueries(
  queryClient: QueryClient,
  payload: InvalidatePayload,
) {
  const squareIds = new Set(payload.squareIds.map(String))
  const currentSquareIds = new Set(payload.currentSquareIds.map(String))
  const applicationId = payload.applicationId != null ? String(payload.applicationId) : null

  const invalidations: Array<Promise<unknown>> = []
  invalidations.push(queryClient.invalidateQueries({ queryKey: ['bootstrap'] }))
  if (payload.refreshDashboard) {
    invalidations.push(queryClient.invalidateQueries({ queryKey: ['dashboard'] }))
  }

  invalidations.push(
    queryClient.invalidateQueries({
      predicate: (query) => {
        const [scope, identifier] = query.queryKey

        if (scope === 'square-detail') {
          return squareIds.size === 0 || squareIds.has(String(identifier ?? ''))
        }
        if (scope === 'current-square') {
          return currentSquareIds.size === 0 || currentSquareIds.has(String(identifier ?? ''))
        }
        if (scope === 'application-detail') {
          if (!payload.refreshApplicationDetail) {
            return false
          }
          return applicationId === null || applicationId === String(identifier ?? '')
        }
        return false
      },
    }),
  )

  return Promise.all(invalidations)
}

const CALL_CARD_POSITION_KEY = 'mango-call-card-position'

export function CrmRealtimeListener() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const seenEventIdsRef = useRef(new Set<string>())
  const orderedEventIdsRef = useRef<string[]>([])
  const [incomingCalls, setIncomingCalls] = useState<IncomingCall[]>([])
  const dragStackRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef<{ offsetX: number; offsetY: number } | null>(null)
  const [cardPosition, setCardPosition] = useState<{ x: number; y: number } | null>(() => {
    try {
      const raw = window.localStorage.getItem(CALL_CARD_POSITION_KEY)
      return raw ? (JSON.parse(raw) as { x: number; y: number }) : null
    } catch {
      return null
    }
  })

  const onHandlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = dragStackRef.current?.getBoundingClientRect()
    if (!rect) {
      return
    }
    dragStateRef.current = { offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top }
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const onHandlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = dragStateRef.current
    if (!state) {
      return
    }
    const rect = dragStackRef.current?.getBoundingClientRect()
    const width = rect?.width ?? 360
    const height = rect?.height ?? 200
    const x = Math.min(Math.max(0, event.clientX - state.offsetX), window.innerWidth - width)
    const y = Math.min(Math.max(0, event.clientY - state.offsetY), window.innerHeight - height)
    setCardPosition({ x, y })
  }

  const onHandlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current) {
      return
    }
    dragStateRef.current = null
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    setCardPosition((current) => {
      if (current) {
        try {
          window.localStorage.setItem(CALL_CARD_POSITION_KEY, JSON.stringify(current))
        } catch {
          // localStorage может быть недоступен — не критично
        }
      }
      return current
    })
  }

  const dragHandleProps = {
    onPointerDown: onHandlePointerDown,
    onPointerMove: onHandlePointerMove,
    onPointerUp: onHandlePointerUp,
  }

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      return undefined
    }

    const eventSource = new window.EventSource(`${API_BASE_URL}/realtime/stream`, {
      withCredentials: true,
    })

    eventSource.onopen = () => {
      void softRefreshCrmQueries(queryClient)
    }

    const handleInvalidate = (event: Event) => {
      const messageEvent = event as MessageEvent<string>
      let payload: InvalidatePayload
      try {
        payload = JSON.parse(messageEvent.data)
      } catch {
        return
      }

      if (
        rememberEvent(
          seenEventIdsRef.current,
          orderedEventIdsRef.current,
          payload.eventId,
        )
      ) {
        return
      }

      void invalidateRealtimeQueries(queryClient, payload)
    }

    const handleApplicationCreated = (event: Event) => {
      const messageEvent = event as MessageEvent<string>
      let payload: ApplicationCreatedPayload
      try {
        payload = JSON.parse(messageEvent.data)
      } catch {
        return
      }

      if (
        rememberEvent(
          seenEventIdsRef.current,
          orderedEventIdsRef.current,
          payload.eventId,
        )
      ) {
        return
      }

      toast.custom((toastId) => (
        <RealtimeApplicationToast
          applicationNumber={payload.applicationNumber ?? payload.applicationId}
          onClose={() => toast.dismiss(toastId)}
          onOpen={() => {
            toast.dismiss(toastId)
            navigate(payload.path)
          }}
          schemaName={payload.schemaName}
          squareNames={payload.squareNames}
          stageName={payload.stageName}
        />
      ), {
        className: 'bg-transparent border-none shadow-none p-0',
        duration: 12_000,
        unstyled: true,
      })
      void invalidateRealtimeQueries(queryClient, {
        applicationId: payload.applicationId,
        currentSquareIds: [],
        eventId: payload.eventId,
        refreshApplicationDetail: true,
        refreshDashboard: true,
        squareIds: payload.squareIds,
      })
    }

    const handleCallIncoming = (event: Event) => {
      const messageEvent = event as MessageEvent<string>
      let payload: CallIncomingPayload
      try {
        payload = JSON.parse(messageEvent.data)
      } catch {
        return
      }
      if (rememberEvent(seenEventIdsRef.current, orderedEventIdsRef.current, payload.eventId)) {
        return
      }
      setIncomingCalls((current) =>
        current.some((call) => call.callId === payload.callId)
          ? current
          : [...current, { callId: payload.callId, fromNumber: payload.fromNumber }],
      )
      // страховка: снять карточку, если событие завершения не придёт
      window.setTimeout(() => {
        setIncomingCalls((current) => current.filter((call) => call.callId !== payload.callId))
      }, 60_000)
    }

    const handleCallEnded = (event: Event) => {
      const messageEvent = event as MessageEvent<string>
      let payload: CallEndedPayload
      try {
        payload = JSON.parse(messageEvent.data)
      } catch {
        return
      }
      setIncomingCalls((current) => current.filter((call) => call.callId !== payload.callId))
    }

    eventSource.addEventListener('crm.invalidate', handleInvalidate)
    eventSource.addEventListener(
      'notification.application_created',
      handleApplicationCreated,
    )
    eventSource.addEventListener('call.incoming', handleCallIncoming)
    eventSource.addEventListener('call.ended', handleCallEnded)

    return () => {
      eventSource.removeEventListener('crm.invalidate', handleInvalidate)
      eventSource.removeEventListener(
        'notification.application_created',
        handleApplicationCreated,
      )
      eventSource.removeEventListener('call.incoming', handleCallIncoming)
      eventSource.removeEventListener('call.ended', handleCallEnded)
      eventSource.close()
    }
  }, [navigate, queryClient])

  return (
    <div
      className="pointer-events-none fixed z-[60] flex flex-col gap-3"
      ref={dragStackRef}
      style={cardPosition ? { left: cardPosition.x, top: cardPosition.y } : { right: 24, bottom: 24 }}
    >
      {incomingCalls.map((call) => (
        <div className="pointer-events-auto" key={call.callId}>
          <IncomingCallCard
            call={call}
            dragHandleProps={dragHandleProps}
            onClose={() =>
              setIncomingCalls((current) => current.filter((item) => item.callId !== call.callId))
            }
          />
        </div>
      ))}
    </div>
  )
}
